import Button from '@/components/Button/Button';
import Header from '@/components/Header/Header';
import { useSTKContext } from '@/context/context';
import {
  getDinamicHeaders,
  getStoreFilteredProducts,
  getStoreGroups,
  getStores,
} from '@/services/menu/menus.helper.service';
import { useMenuProductsGroup, useProductCategories, useVariant } from '@/services/menu/menus.service';
import { FilteredValues, StoreProduct } from '@/types/menus.types';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { Box, Chip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FZStoreMenuDetails.module.css';
import FZStoreItemTable from './internal/FZStoreItemTable';
import FZStoreMenuFilter from './internal/FZStoreMenuFilter';
import ModalStores from './internal/ModalStores.module';

import Tooltip from '@/components/Tooltip/Tooltip';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useCurrentUser } from '@/services/user/user.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { RowSelectionState } from '@tanstack/react-table';
import classNames from 'classnames';
import { EditPriceDrawer } from '../EditPriceDrawer/EditPriceDrawer';
import ModalLockedStores from './internal/ModalLockedProducts';
import { UserRoles, UserTypes } from '@/types/auth.types';
import { PriceDeviationsDrawer } from '../PriceDeviationsDrawer/PriceDeviationsDrawer';

const DEFAULT_CATEGORY_NAME = 'Pizza';
const provideNewSelectedRows = (selectedRows: RowSelectionState | undefined, storeProducts: StoreProduct[]) => {
  const selectedStores = new Set(Object.keys(selectedRows as RowSelectionState));
  const newSelectedRows: StoreProduct[] = [];
  selectedStores.forEach((index) => {
    newSelectedRows.push(storeProducts[+index]);
  });
  return newSelectedRows;
};

const FZStoreMenuDetails = () => {
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { storesAndGroups } = useSTKContext();
  const [stores, setStores] = useState<string[]>(getStores(storesAndGroups));
  const [storeGroups, setStoreGroups] = useState(getStoreGroups(storesAndGroups));
  const [variantId, setVariantId] = useState('');
  const [isEditPriceDrawerOpen, setIsEditPriceDrawerOpen] = useState<boolean>(false);
  const [priceDeviationsData, setPriceDeviationsData] = useState({
    isPriceDeviationsDrawerOpen: false,
    variantIds: [],
    selectedGroupName: '',
    cameFromEditDrawer: false,
  });
  const [storeProducts, setStoreProducts] = useState<StoreProduct[]>();
  const { data: dataUser } = useCurrentUser(true);
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);

  const isFranchiseeUserEdit = dataUser?.data.type === UserTypes.FRANCHISEE && dataUser?.data.role === UserRoles.EDIT;
  const isUserViewOnlyAccess = dataUser?.data.role === UserRoles.VIEW;
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const isFranchiseeEditAndFreeze = isFranchiseeUserEdit && freezeExists;

  const [filteredValues, setFilteredValues] = useState<FilteredValues>({});
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [filterApplied, setFilterApplied] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLockedOpen, setModalLockedOpen] = useState(false);
  const [categoryName, setCategoryName] = useState<string>(DEFAULT_CATEGORY_NAME);
  const [subCategories, setSubCategories] = useState<
    | {
        value: string;
        label: string;
      }[]
    | undefined
  >([]);

  const { data: productCategories = [], isLoading: isLoadingCategories = false } = useProductCategories(
    true,
    stores,
    [],
  );
  const { data: variant, isLoading: isVariantLoading } = useVariant(!!variantId, stores, storeGroups, variantId);
  const {
    data: menuProductsGroup = [],
    isLoading: isStoreLoading,
    isFetching,
    refetch: refetchProducts,
  } = useMenuProductsGroup(filterApplied, stores, storeGroups, categoryName);

  const [headerWidth, setHeaderWidth] = useState('');

  const changeHeaderWidth = (newWidth: string) => {
    setHeaderWidth(newWidth);
  };
  const applyFilter = (pFilteredValues: FilteredValues) => {
    setFilteredValues(pFilteredValues);
    setFilterApplied(true);
    setSelectedRows({});
  };
  const handleEditPriceDrawerClose = () => {
    setIsEditPriceDrawerOpen(false);
  };

  const handlePriceDeviationsDrawerClose = () => {
    setPriceDeviationsData({ ...priceDeviationsData, isPriceDeviationsDrawerOpen: false });
  };

  const clearFilter = () => {
    setFilteredValues({});
    setCategoryName(categoryName);
  };

  const categories = productCategories.map((productCategory) => ({
    value: productCategory.category,
    label: productCategory.category,
  }));

  const products = useMemo(
    () => getStoreFilteredProducts(menuProductsGroup, filteredValues),
    [menuProductsGroup, filteredValues],
  );
  const lockedProducts = useMemo(
    () => products.filter((prod) => prod.priceLock && prod.mandatoryProductLock),
    [products],
  );

  useEffect(() => {
    const categoryWithSubCategory = productCategories.find(
      (productCategory) => productCategory.category === categoryName,
    );
    if (categoryWithSubCategory) {
      const subcats = categoryWithSubCategory.subcategories?.map((productCategory) => ({
        value: productCategory.productClassCode,
        label: productCategory.subcategory,
      }));
      setSubCategories(subcats);
    }
  }, [categoryName, productCategories]);

  const handleEditPriceDrawerOpen = useCallback(() => {
    const selectedProducts = provideNewSelectedRows(selectedRows, products);
    setStoreProducts(selectedProducts);
    setIsEditPriceDrawerOpen(true);
  }, [products, selectedRows]);

  const handleCategory = (newCategory: string) => {
    setFilteredValues({});
    setCategoryName(newCategory);
  };

  useEffect(() => {
    const selectedCount = Object.keys(selectedRows).length;
    if (selectedCount !== 0 && lockedProducts.length !== 0 && products.length === selectedCount) {
      setModalLockedOpen(true);
    }
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows({});
  }, [categoryName]);

  return (
    <div className={styles.page} data-testid="fzStoreMenu">
      <div>
        <div style={{ minWidth: 'fit-content' }}>
          <Header
            title="Menu"
            breadcrumb={{ label: t('breadcrumb.backToAllMenus'), to: '/menus' }}
            includeCalenderLink
          />
        </div>
        <div className={styles.chip}>
          <Chip
            data-testid="icon"
            key="storeCount"
            label={t('menu.storesLabel', { count: stores?.length ?? 1 })}
            color="default"
            variant="outlined"
            onClick={() => setModalOpen(!modalOpen)}
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              height: '28px',
              backgroundColor: 'white',
              color: '#266DCC',
              borderColor: '#266DCC',
              marginLeft: '160px',
              marginTop: '-135px',
            }}
          />
        </div>
      </div>
      <div className={classNames(styles.header, { [styles.withAlias]: alias })} style={{ minWidth: headerWidth }}>
        <div className={styles.headerLabelAndLegendContainer}>
          <span>{t('menu.filter.products')}</span>
          <span className={styles.legend}>
            <Tooltip
              position="top"
              size={22}
              content={
                <div className={styles.contentColor}>
                  <div className={styles.labelPosition}>
                    <CircleIcon sx={{ color: '#999999' }} />
                    &nbsp; {t('menu.legend.allStoreMenus')}
                  </div>
                  <div className={styles.labelPosition}>
                    <CircleOutlinedIcon sx={{ color: '#999999' }} />
                    &nbsp; {t('menu.legend.someStoreMenus')}
                  </div>
                  <div className={styles.labelPosition}>{t('menu.legend.dealLovers')}</div>
                  <div>{t('menu.legend.virtualBrand')}</div>
                </div>
              }
            />
            <span className={styles.legendLabel}>{t('menu.legend.legend')}</span>
          </span>
          {!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess && (
            <Button onClick={handleEditPriceDrawerOpen} disabled={Object.keys(selectedRows).length < 1}>
              {t('menu.editMenuPriceButton')}
            </Button>
          )}
        </div>
      </div>
      <div className={styles.filterActions}>
        <FZStoreMenuFilter
          onApplyFilter={(pFilteredValues: FilteredValues) => applyFilter(pFilteredValues)}
          onClearFilter={clearFilter}
          categories={categories}
          categoryName={categoryName}
          onCategoryChange={(newCategory: string) => handleCategory(newCategory)}
          subCategories={subCategories || []}
          isLoadingCategories={isLoadingCategories}
          searching={isFetching}
        />
      </div>
      {filterApplied && !isStoreLoading && (
        <Box>
          <Box>
            {isStoreLoading || products.length === 0 ? (
              <Box pl={{ sm: 5, md: 7 }} pt={{ sm: 2, md: 4 }} fontSize="2.0rem">
                <p> No products match your search for these stores </p>
              </Box>
            ) : (
              <FZStoreItemTable
                menuProducts={products}
                dynamicHeaders={getDinamicHeaders(storesAndGroups)}
                setPriceDeviationsData={setPriceDeviationsData}
                selectedRows={selectedRows}
                onRowSelectionChange={setSelectedRows}
                variant={variant}
                onHandleSetVariantId={(pVariantId: any) => setVariantId(pVariantId)}
                isLoading={isVariantLoading}
                changeHeaderSize={changeHeaderWidth}
                storesAndGroups={storesAndGroups}
                isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
              />
            )}
          </Box>
        </Box>
      )}

      {modalOpen && <ModalStores stores={stores} isOpen={modalOpen} onClose={() => setModalOpen(false)} />}
      {modalLockedOpen && (
        <ModalLockedStores
          lockedProducts={lockedProducts}
          isOpen={modalLockedOpen}
          onClose={() => setModalLockedOpen(false)}
        />
      )}

      {isEditPriceDrawerOpen && (
        <EditPriceDrawer
          isOpen={isEditPriceDrawerOpen}
          onClose={handleEditPriceDrawerClose}
          storeProducts={storeProducts || []}
          afterSave={() => {
            setSelectedRows({});
            refetchProducts();
          }}
          setPriceDeviationsData={setPriceDeviationsData}
        />
      )}
      {priceDeviationsData.isPriceDeviationsDrawerOpen && (
        <PriceDeviationsDrawer
          isOpen={priceDeviationsData.isPriceDeviationsDrawerOpen}
          onClose={handlePriceDeviationsDrawerClose}
          variantIds={priceDeviationsData.variantIds}
          selectedGroupName={priceDeviationsData.selectedGroupName}
          cameFromEditDrawer={priceDeviationsData.cameFromEditDrawer}
        />
      )}
    </div>
  );
};
export default FZStoreMenuDetails;
